// src/pages/Onboarding/slides/ContactInfoSlide.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import SlideShell from '../SlideShell';
import { parsePhoneNumber, isPossiblePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import { getSortedCountries, getCountryName, detectCountryFromLocale, isValidCountryCode } from '../../../utils/phoneCountryUtils';
import { trackEvent } from '../../../utils/Tracking';
import { trackMetaLead } from '../../../utils/metaPixel';
import { trackTikTokContact } from '../../../utils/tikTokPixel';

import BulletList from '../../../components/Onboarding/BulletList';
import lockedIcon from '../../../images/optionIcons/locked.svg';
import WhatsAppLogo from '../../../images/WhatsAppLogo.png';

export default function ContactInfoSlide({
  title = 'What is your phone number?',
  subtitle = "We'll use this to share your results and next steps via SMS/WhatsApp.",
  currentStep,
  totalSteps,
  onNext,
  onBack,
  initialPhone = '',
  initialCountryDial = '+1',
  initialCountryCode = 'US',
  fieldName = 'primaryContactPhoneNumber',
  dialCodeFieldName = 'primaryContactDialCode',
  countryFieldName = 'primaryContactPhoneRegion',
  preferredChannelFieldName = 'preferredFollowUpChannel',
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',
  showFooterSubtext = false,
  footerSubtextText = '',
  footerSubtextProps = {},
  onAutoSave = null,
  onGoToEmail = () => {},
  sessionId = null,
}) {
  const isDark = theme === 'dark';

  // Selected country — restore saved session value if available, otherwise detect from browser locale
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const code = initialCountryCode?.toUpperCase();
    if (isValidCountryCode(code)) return code;
    return detectCountryFromLocale();
  });

  // Phone number input (WITHOUT country code - just the local number)
  const [phoneInput, setPhoneInput] = useState(() => {
    // Extract local number from initialPhone if present
    if (initialPhone && initialPhone.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(initialPhone);
        if (parsed) {
          return parsed.nationalNumber || '';
        }
      } catch (e) {
        // If parsing fails, try to strip the dial code
        const dialCode = `+${getCountryCallingCode(selectedCountry)}`;
        return initialPhone.replace(dialCode, '').trim();
      }
    }
    return '';
  });

  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const lastCommittedRef = useRef(null);
  const isInitialAutoSaveRef = useRef(true);

  // Get sorted countries for dropdown (priority markets first, then A→Z by name)
  const { priority: priorityCountries, rest: restCountries } = getSortedCountries();

  // Build full E.164 phone number
  const getFullPhoneNumber = useCallback(() => {
    if (!phoneInput || !phoneInput.trim()) {
      return '';
    }
    const dialCode = getCountryCallingCode(selectedCountry);
    // Ensure phoneInput doesn't already have a + or country code
    const cleanedInput = phoneInput.replace(/^\+\d+/, '').trim();
    return `+${dialCode}${cleanedInput}`;
  }, [phoneInput, selectedCountry]);

  const buildPayload = useCallback(() => {
    if (!phoneInput || !phoneInput.trim()) {
      return {
        [fieldName]: '',
        [dialCodeFieldName]: '',
        [countryFieldName]: '',
      };
    }

    const fullNumber = getFullPhoneNumber();
    const dialCode = `+${getCountryCallingCode(selectedCountry)}`;

    return {
      [fieldName]: fullNumber,           // Full E.164 format: "+12133734253"
      [dialCodeFieldName]: dialCode,      // Dial code: "+1"
      [countryFieldName]: selectedCountry // ISO code: "US"
    };
  }, [phoneInput, selectedCountry, fieldName, dialCodeFieldName, countryFieldName, getFullPhoneNumber]);

  const commitAutoSave = useCallback(() => {
    if (!onAutoSave) return;
    const payload = buildPayload();
    const serialized = JSON.stringify(payload);
    if (isInitialAutoSaveRef.current) {
      lastCommittedRef.current = serialized;
      isInitialAutoSaveRef.current = false;
      return;
    }
    if (lastCommittedRef.current === serialized) return;
    lastCommittedRef.current = serialized;
    onAutoSave(payload);
  }, [buildPayload, onAutoSave]);

  useEffect(() => {
    commitAutoSave();
  }, [phoneInput, selectedCountry, commitAutoSave]);

  const validatePhone = () => {
    setError('');
    
    if (!phoneInput || !phoneInput.trim()) {
      setError('Please enter your phone number.');
      return false;
    }

    const fullNumber = getFullPhoneNumber();
    
    // Use isPossiblePhoneNumber for lenient validation
    if (!isPossiblePhoneNumber(fullNumber)) {
      setError('Please enter a valid phone number.');
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    if (!validatePhone()) return;

    commitAutoSave();

    // Track Meta Pixel Lead event for phone number submission
    trackMetaLead({
      content_name: 'contact_info_phone',
      content_category: 'onboarding_contact',
    });
    // Track TikTok Pixel Contact event for phone number submission
    trackTikTokContact('onboarding_phone');

    const payload = buildPayload();
    onNext?.({
      ...payload,
      [preferredChannelFieldName]: 'phone',
    });
  };

  const disabled = !phoneInput || !phoneInput.trim() || !!error;

  const labelText = isDark ? 'text-white' : 'text-gray-900';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={handleContinue}
        onBack={onBack}
        nextLabel="Continue"
        disabled={disabled}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        freezeViewportVars
        showFooterSubtext={showFooterSubtext}
        footerSubtextText={footerSubtextText}
        footerSubtextProps={footerSubtextProps}
        footer={(
          <div className="space-y-3">
            {/* WhatsApp secondary button */}
            <a
              href="https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services."
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                // Track new chat button click
                const clickEntry = {
                  slideName: 'contact_info',
                  slideNumber: 28,
                  buttonType: 'whatsapp',
                  href: 'https://wa.me/908503469160',
                  timestamp: new Date().toISOString(),
                };
                if (onAutoSave) {
                  onAutoSave({ contactButtonClicks: [clickEntry] });
                }
                trackEvent('new_chat_button_clicked', {
                  button_type: 'whatsapp',
                  href: 'https://wa.me/908503469160',
                  source_type: 'onboarding',
                  source_detail: 'contact_info_slide',
                  onboarding_id: sessionId || null,
                  slide_number: 28,
                });
              }}
              className="w-full py-3 px-6 rounded-full md:rounded-lg border border-white bg-[#1f1f1f] hover:bg-black transition inline-flex items-center justify-center gap-2"
            >
              <span className="text-white text-sm font-medium whitespace-nowrap">Contact us directly with</span>
              <img src={WhatsAppLogo} alt="WhatsApp" className="h-5 w-5 object-contain flex-shrink-0" />
            </a>
            {/* Primary button */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={disabled}
              className={`w-full py-3 px-6 rounded-full md:rounded-lg transition inline-flex items-center justify-center text-sm font-medium ${
                isDark
                  ? 'bg-white text-mm_black hover:bg-gray-100 disabled:bg-gray-600 disabled:cursor-not-allowed'
                  : 'bg-mm_black text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed'
              }`}
            >
              Continue with phone number
            </button>
          </div>
        )}
      >
        <div className="w-full max-w-md mx-auto">
          <label className={`block mb-2 text-sm font-medium ${labelText}`}>
            Your phone number
          </label>

          <div className="flex gap-2 items-center">
            {/* Country Selector - Shows flag only when closed */}
            <div className="relative flex-shrink-0 flex items-center gap-1">
              <select
                value={selectedCountry}
                onChange={(e) => {
                  const countryCode = e.target.value;
                  // Use ISO country code directly - no ambiguity from shared calling codes
                  setSelectedCountry(countryCode);
                  
                  if (error) setError('');
                  // Focus input after country change
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                autoComplete="tel-country-code"
                className="phone-country-select h-12 w-12 cursor-pointer bg-transparent border-0 focus:outline-none appearance-none"
                style={{
                  backgroundImage: `url(https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry}.svg)`,
                  backgroundSize: '36px 24px',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  color: 'transparent',
                }}
              >
                {priorityCountries.map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)} +{getCountryCallingCode(country)}
                  </option>
                ))}
                <option disabled>──────────────</option>
                {restCountries.map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)} +{getCountryCallingCode(country)}
                  </option>
                ))}
              </select>
              {/* Dropdown arrow - positioned next to flag */}
              <div className="pointer-events-none -ml-2">
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path 
                    d="M1 1L5 5L9 1" 
                    stroke={isDark ? '#e5e7eb' : '#111827'} 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Phone Input with Country Code Prefix */}
            <div className="flex-1 relative">
              <div 
                className={`
                  flex items-center h-10 px-4 rounded-xl border
                  ${isDark ? 'bg-transparent border-gray-600' : 'bg-white border-gray-300'}
                  focus-within:ring-2
                  ${isDark ? 'focus-within:ring-white' : 'focus-within:ring-black'}
                  transition-all
                `}
              >
                {/* Non-editable country code prefix */}
                <span 
                  className={`
                    font-medium select-none pointer-events-none mr-1
                    ${isDark ? 'text-gray-400' : 'text-gray-500'}
                  `}
                >
                  +{getCountryCallingCode(selectedCountry)}
                </span>
                
                {/* Actual input field */}
                <input
                  ref={inputRef}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  name="phone-national"
                  value={phoneInput}
                  onChange={(e) => {
                    // Only allow digits, spaces, hyphens, parentheses
                    const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                    setPhoneInput(cleaned);
                    if (error) setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleContinue();
                    }
                  }}
                  placeholder="555 123 4567"
                  className={`
                    flex-1 bg-transparent border-none outline-none
                    ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}
                  `}
                />
              </div>
            </div>
          </div>

          <div className="mt-7">
            <BulletList
              items={[
                (
                  <>
                    Your data will be secured with industry standard <strong>AES-256 encryption</strong>
                    <img
                      src={lockedIcon}
                      alt=""
                      className="inline-block ml-1 w-[0.9em] h-[0.8em] align-[-0.05em] opacity-90"
                    />
                  </>
                ),
                'We will never spam you',
                'Many clinics prefer consultation via mobile messaging apps',
                (
                  <>
                    To skip this step{' '}
                    <button
                      type="button"
                      className="font-medium cursor-pointer inline-flex items-center"
                      onClick={onGoToEmail}
                    >
                      <strong>click here</strong>
                    </button>
                    {/* Arrow is the same text arrow used in Hero CTA */}
                    <span className="font-semibold"> →</span>
                  </>
                ),
              ]}
              theme={theme}
              delayMs={400}
            />
          </div>

          {error ? (
            <div className="mt-2 text-sm text-red-500">{error}</div>
          ) : null}
        </div>
      </SlideShell>
    </div>
  );
}
